// Simplified Google Maps loader with better error handling
class GoogleMapsLoader {
  constructor() {
    this.isLoaded = false;
    this.isLoading = false;
    this.callbacks = [];
    this.apiKey = 'AIzaSyCsEuLdHnu5Iyo1xtAe0xg-VGlZ0_xWyhs';
    this.loadAttempts = 0;
    this.maxAttempts = 3;
  }

  load() {
    return new Promise((resolve, reject) => {
      console.log('GoogleMapsLoader: Load requested, current state:', {
        isLoaded: this.isLoaded,
        isLoading: this.isLoading,
        hasWindow: typeof window !== 'undefined',
        hasGoogle: !!(window.google && window.google.maps),
        loadAttempts: this.loadAttempts,
        apiKey: this.apiKey ? 'Present' : 'Missing',
        existingScripts: document.querySelectorAll('script[src*="maps.googleapis.com"]').length
      });

      // If already loaded, resolve immediately
      if (this.isLoaded && window.google && window.google.maps) {
        console.log('GoogleMapsLoader: Already loaded, resolving immediately');
        resolve(window.google.maps);
        return;
      }

      // Check if Google Maps is already available (loaded by another script)
      if (window.google && window.google.maps && !this.isLoaded) {
        console.log('GoogleMapsLoader: Google Maps found in window, marking as loaded');
        this.isLoaded = true;
        resolve(window.google.maps);
        return;
      }

      // Add callback to queue
      this.callbacks.push({ resolve, reject });
      console.log('GoogleMapsLoader: Added callback to queue, total callbacks:', this.callbacks.length);

      // If already loading, just wait
      if (this.isLoading) {
        console.log('GoogleMapsLoader: Already loading, waiting...');
        return;
      }

      // Check if we've exceeded max attempts
      if (this.loadAttempts >= this.maxAttempts) {
        console.error('GoogleMapsLoader: Max load attempts exceeded');
        const error = new Error('Failed to load Google Maps API after multiple attempts');
        this.callbacks.forEach(({ reject }) => reject(error));
        this.callbacks = [];
        return;
      }

      // Start loading
      this.isLoading = true;
      this.loadAttempts++;
      console.log('GoogleMapsLoader: Starting load attempt', this.loadAttempts);

      // Remove any existing scripts first to avoid conflicts
      const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
      console.log('GoogleMapsLoader: Found', existingScripts.length, 'existing Google Maps scripts');
      existingScripts.forEach((script, index) => {
        console.log(`GoogleMapsLoader: Removing existing script ${index + 1}:`, script.src);
        script.remove();
      });

      // Clean up any existing global callbacks
      Object.keys(window).forEach(key => {
        if (key.startsWith('initGoogleMaps')) {
          console.log('GoogleMapsLoader: Cleaning up existing callback:', key);
          delete window[key];
        }
      });

      // Create unique callback name
      const callbackName = 'initGoogleMaps_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      console.log('GoogleMapsLoader: Using callback name:', callbackName);

      // Create and load script
      console.log('GoogleMapsLoader: Creating new script element');
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.id = 'google-maps-script-' + Date.now();

      console.log('GoogleMapsLoader: Script URL:', script.src);
      console.log('GoogleMapsLoader: Script ID:', script.id);

      // Set up global callback
      window[callbackName] = () => {
        console.log('GoogleMapsLoader: Global callback triggered for', callbackName);
        console.log('GoogleMapsLoader: window.google available:', !!(window.google));
        console.log('GoogleMapsLoader: window.google.maps available:', !!(window.google && window.google.maps));
        
        if (window.google && window.google.maps) {
          this.isLoaded = true;
          this.isLoading = false;
          
          // Resolve all pending callbacks
          console.log('GoogleMapsLoader: Resolving', this.callbacks.length, 'callbacks');
          this.callbacks.forEach(({ resolve }) => {
            try {
              resolve(window.google.maps);
            } catch (error) {
              console.error('GoogleMapsLoader: Error resolving callback:', error);
            }
          });
          this.callbacks = [];
          
          // Clean up global callback
          delete window[callbackName];
          console.log('GoogleMapsLoader: Successfully loaded Google Maps');
        } else {
          console.error('GoogleMapsLoader: Callback triggered but Google Maps not available');
          const error = new Error('Google Maps callback triggered but API not available');
          this.callbacks.forEach(({ reject }) => reject(error));
          this.callbacks = [];
          this.isLoading = false;
          delete window[callbackName];
        }
      };

      script.onload = () => {
        console.log('GoogleMapsLoader: Script onload event fired');
        // Note: The actual initialization happens in the callback, not onload
      };

      script.onerror = (error) => {
        console.error('GoogleMapsLoader: Script loading failed:', error);
        console.error('GoogleMapsLoader: Script src was:', script.src);
        console.error('GoogleMapsLoader: Error type:', error.type);
        console.error('GoogleMapsLoader: Error target:', error.target);
        
        this.isLoading = false;
        const loadError = new Error(`Failed to load Google Maps API script (attempt ${this.loadAttempts}): ${error.message || error.type || 'Unknown error'}`);
        
        // Clean up global callback
        delete window[callbackName];
        
        // If we haven't exceeded max attempts, try again
        if (this.loadAttempts < this.maxAttempts) {
          console.log('GoogleMapsLoader: Retrying in 3 seconds...');
          setTimeout(() => {
            this.isLoading = false;
            this.load().then(
              (maps) => {
                console.log('GoogleMapsLoader: Retry successful');
                // Don't resolve here, callbacks are already handled
              },
              (err) => {
                console.error('GoogleMapsLoader: Retry failed:', err);
                // Don't reject here, callbacks are already handled
              }
            );
          }, 3000);
        } else {
          // Reject all pending callbacks
          console.error('GoogleMapsLoader: All retry attempts exhausted');
          this.callbacks.forEach(({ reject }) => {
            reject(loadError);
          });
          this.callbacks = [];
        }
      };

      console.log('GoogleMapsLoader: Appending script to document head');
      document.head.appendChild(script);
      
      // Set up a timeout as fallback
      const timeoutId = setTimeout(() => {
        if (this.isLoading && !this.isLoaded) {
          console.warn('GoogleMapsLoader: Loading timeout (20s), checking if Google Maps is available...');
          if (window.google && window.google.maps) {
            console.log('GoogleMapsLoader: Google Maps found despite timeout, resolving...');
            this.isLoaded = true;
            this.isLoading = false;
            this.callbacks.forEach(({ resolve }) => resolve(window.google.maps));
            this.callbacks = [];
            // Clean up global callback
            delete window[callbackName];
          } else {
            console.error('GoogleMapsLoader: Timeout - Google Maps still not available after 20s');
            const timeoutError = new Error('Timeout loading Google Maps API (20 seconds)');
            this.callbacks.forEach(({ reject }) => reject(timeoutError));
            this.callbacks = [];
            this.isLoading = false;
            // Clean up global callback
            delete window[callbackName];
          }
        }
      }, 20000); // 20 second timeout

      // Store timeout ID for cleanup
      script.timeoutId = timeoutId;
    });
  }

  // Method to check current status
  getStatus() {
    return {
      isLoaded: this.isLoaded,
      isLoading: this.isLoading,
      loadAttempts: this.loadAttempts,
      hasGoogle: !!(window.google && window.google.maps),
      pendingCallbacks: this.callbacks.length,
      existingScripts: document.querySelectorAll('script[src*="maps.googleapis.com"]').length
    };
  }

  // Method to force reset
  reset() {
    console.log('GoogleMapsLoader: Force reset requested');
    this.isLoaded = false;
    this.isLoading = false;
    this.loadAttempts = 0;
    this.callbacks = [];
    
    // Clean up existing scripts
    const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    existingScripts.forEach(script => script.remove());
    
    // Clean up global callbacks
    Object.keys(window).forEach(key => {
      if (key.startsWith('initGoogleMaps')) {
        delete window[key];
      }
    });
  }
}

// Export singleton instance
export const googleMapsLoader = new GoogleMapsLoader();
export default googleMapsLoader;
